import { useState, useEffect, useCallback } from 'react';

/**
 * Hook personalizado para manejar los triggers inteligentes del popup promocional
 * Basado en comportamiento del usuario y eventos del juego
 */
const useCoursePromoTriggers = () => {
  const [shouldShow, setShouldShow] = useState(false);
  const [userBehavior, setUserBehavior] = useState({
    levelsCompleted: 0,
    totalXp: 0,
    closeCount: 0,
    lastShown: null,
    hasClickedCTA: false,
    ctaClickedAt: null,
    firstVisit: true,
    profileVisits: 0,
    achievements: []
  });

  // Cargar comportamiento del usuario desde localStorage
  useEffect(() => {
    const savedBehavior = localStorage.getItem('coursePromoUserBehavior');
    if (savedBehavior) {
      setUserBehavior(JSON.parse(savedBehavior));
    }
  }, []);

  // Guardar comportamiento del usuario
  const saveBehavior = useCallback((newBehavior) => {
    const updated = { ...userBehavior, ...newBehavior };
    setUserBehavior(updated);
    localStorage.setItem('coursePromoUserBehavior', JSON.stringify(updated));
  }, [userBehavior]);

  // Verificar si debe mostrar el popup basado en frecuencia y nuevas reglas
  const shouldShowBasedOnFrequency = useCallback(() => {
    const { lastShown, closeCount, ctaClickedAt } = userBehavior;

    const now = new Date();

    // REGLA 1: Si hizo clic en el botón, no mostrar durante 2 días (48 horas)
    if (ctaClickedAt) {
      const ctaClickedDate = new Date(ctaClickedAt);
      const hoursSinceClick = (now - ctaClickedDate) / (1000 * 60 * 60);

      if (hoursSinceClick < 48) {
        return false;
      }
    }

    // REGLA 2: Si cerró más de 5 veces, no mostrar hasta el día siguiente
    if (closeCount >= 5 && lastShown) {
      const lastShownDate = new Date(lastShown);

      // Comparar solo las fechas (año, mes, día), no las horas
      const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const lastShownDateOnly = new Date(
        lastShownDate.getFullYear(),
        lastShownDate.getMonth(),
        lastShownDate.getDate()
      );

      // Si la fecha actual es la misma que lastShown, no mostrar
      if (nowDate.getTime() === lastShownDateOnly.getTime()) {
        return false;
      }

      // Si cambió el día, resetear el contador de cierres
      // Esto se hará en handleClose, pero aquí permitimos mostrar
    }

    // Si nunca se ha mostrado, permitir
    if (!lastShown) {
      return true;
    }

    const lastShownDate = new Date(lastShown);
    const minutesSinceLastShown = (now - lastShownDate) / (1000 * 60);

    // Mostrar cada 5-10 minutos (promedio 7.5 minutos)
    return minutesSinceLastShown >= 5;
  }, [userBehavior]);

  // Detectar triggers basados en comportamiento
  const checkTriggers = useCallback((eventData) => {
    if (!shouldShowBasedOnFrequency()) {
      return;
    }

    const { type, data } = eventData;

    switch (type) {
      case 'LEVEL_COMPLETED':
        const newLevelCount = data.levelsCompleted;

        // Trigger después de completar nivel 1 (primer contacto)
        if (newLevelCount === 1 && userBehavior.levelsCompleted < 1) {
          setShouldShow(true);
          saveBehavior({ levelsCompleted: newLevelCount });
        }

        // Trigger después de completar 3 niveles (usuario comprometido)
        if (newLevelCount === 3 && userBehavior.levelsCompleted < 3) {
          setShouldShow(true);
          saveBehavior({ levelsCompleted: newLevelCount });
        }

        // Trigger después de completar 6 niveles (usuario muy interesado)
        if (newLevelCount === 6 && userBehavior.levelsCompleted < 6) {
          setShouldShow(true);
          saveBehavior({ levelsCompleted: newLevelCount });
        }

        // Trigger después de completar 10 niveles
        if (newLevelCount === 10 && userBehavior.levelsCompleted < 10) {
          setShouldShow(true);
          saveBehavior({ levelsCompleted: newLevelCount });
        }
        break;

      case 'XP_MILESTONE':
        const newXp = data.totalXp;

        // Triggers en hitos de XP (500, 1500, 3000)
        if (newXp >= 500 && userBehavior.totalXp < 500) {
          setShouldShow(true);
          saveBehavior({ totalXp: newXp });
        } else if (newXp >= 1500 && userBehavior.totalXp < 1500) {
          setShouldShow(true);
          saveBehavior({ totalXp: newXp });
        } else if (newXp >= 3000 && userBehavior.totalXp < 3000) {
          setShouldShow(true);
          saveBehavior({ totalXp: newXp });
        }
        break;

      case 'ACHIEVEMENT_UNLOCKED':
        const achievementId = data.achievementId;
        const importantAchievements = ['first_steps', 'constructor', 'master'];

        // Trigger en logros importantes
        if (importantAchievements.includes(achievementId) &&
            !userBehavior.achievements.includes(achievementId)) {
          setShouldShow(true);
          saveBehavior({
            achievements: [...userBehavior.achievements, achievementId]
          });
        }
        break;

      case 'PROFILE_VISIT':
        const profileVisits = userBehavior.profileVisits + 1;

        // Trigger en la primera visita al perfil (momento de reflexión)
        if (profileVisits === 1) {
          setShouldShow(true);
        }

        saveBehavior({ profileVisits });
        break;

      case 'FIRST_VISIT_TIMER':
        // Trigger después de 2-3 minutos en la primera visita
        if (userBehavior.firstVisit) {
          setShouldShow(true);
          saveBehavior({ firstVisit: false });
        }
        break;

      default:
        break;
    }
  }, [userBehavior, shouldShowBasedOnFrequency, saveBehavior]);

  // Marcar popup como cerrado
  const handleClose = useCallback(() => {
    setShouldShow(false);
    const now = new Date();
    const nowISO = now.toISOString();

    // Verificar si cambió el día desde la última vez que se mostró
    let newCloseCount = userBehavior.closeCount + 1;

    if (userBehavior.lastShown) {
      const lastShownDate = new Date(userBehavior.lastShown);
      const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const lastShownDateOnly = new Date(
        lastShownDate.getFullYear(),
        lastShownDate.getMonth(),
        lastShownDate.getDate()
      );

      // Si cambió el día, resetear el contador de cierres
      if (nowDate.getTime() !== lastShownDateOnly.getTime()) {
        newCloseCount = 1; // Reiniciar el contador
      }
    }

    saveBehavior({
      lastShown: nowISO,
      closeCount: newCloseCount
    });
  }, [userBehavior.closeCount, userBehavior.lastShown, saveBehavior]);

  // Marcar que el usuario hizo clic en el CTA
  const handleCTAClick = useCallback(() => {
    setShouldShow(false);
    const now = new Date().toISOString();
    saveBehavior({
      lastShown: now,
      hasClickedCTA: true,
      ctaClickedAt: now
    });
  }, [saveBehavior]);

  // Timer recurrente cada 5-10 minutos (estilo Duolingo)
  useEffect(() => {
    // Función que verifica y muestra el popup
    const checkAndShow = () => {
      const savedBehavior = localStorage.getItem('coursePromoUserBehavior');
      const behavior = savedBehavior ? JSON.parse(savedBehavior) : {
        lastShown: null,
        closeCount: 0,
        ctaClickedAt: null
      };

      const now = new Date();

      // REGLA 1: Si hizo clic en el botón, no mostrar durante 2 días (48 horas)
      if (behavior.ctaClickedAt) {
        const ctaClickedDate = new Date(behavior.ctaClickedAt);
        const hoursSinceClick = (now - ctaClickedDate) / (1000 * 60 * 60);

        if (hoursSinceClick < 48) {
          return; // No mostrar
        }
      }

      // REGLA 2: Si cerró más de 5 veces, no mostrar hasta el día siguiente
      if (behavior.closeCount >= 5 && behavior.lastShown) {
        const lastShownDate = new Date(behavior.lastShown);
        const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const lastShownDateOnly = new Date(
          lastShownDate.getFullYear(),
          lastShownDate.getMonth(),
          lastShownDate.getDate()
        );

        // Si la fecha actual es la misma que lastShown, no mostrar
        if (nowDate.getTime() === lastShownDateOnly.getTime()) {
          return; // No mostrar
        }
      }

      // Verificar si han pasado al menos 5 minutos
      if (!behavior.lastShown) {
        setShouldShow(true);
        const updated = { ...behavior, lastShown: now.toISOString() };
        localStorage.setItem('coursePromoUserBehavior', JSON.stringify(updated));
        return;
      }

      const lastShownDate = new Date(behavior.lastShown);
      const minutesSinceLastShown = (now - lastShownDate) / (1000 * 60);

      if (minutesSinceLastShown >= 5) {
        setShouldShow(true);
        const updated = { ...behavior, lastShown: now.toISOString() };
        localStorage.setItem('coursePromoUserBehavior', JSON.stringify(updated));
      }
    };

    // Timer inicial: mostrar después de 5-10 minutos aleatorios
    const initialDelay = Math.floor(Math.random() * 300000) + 300000; // 5-10 minutos
    const initialTimer = setTimeout(() => {
      checkAndShow();
    }, initialDelay);

    // Timer recurrente: verificar cada minuto si debe mostrar
    const recurringInterval = setInterval(() => {
      checkAndShow();
    }, 60000); // Verificar cada minuto

    return () => {
      clearTimeout(initialTimer);
      clearInterval(recurringInterval);
    };
  }, []); // Sin dependencias para que el timer se mantenga activo

  return {
    shouldShow,
    checkTriggers,
    handleClose,
    handleCTAClick,
    userBehavior
  };
};

export default useCoursePromoTriggers;
